import type { PublicPlan as PublicPlanV2 } from '@wix/ambassador-pricing-plans-v2-plan/types';
import type { Plan as PlanV3 } from '@wix/ambassador-pricing-plans-v3-plan/types';
import type { TranslatedPricingDetails } from '@wix/pricing-plans-tpa-api';
import { getPlanPrice, getPlanPriceV3, isPlanV3 } from '@wix/pricing-plans-utils';
import type { CreateCurrencyFormatter, TFunction } from '@wix/yoshi-flow-editor';
import {
  getFormattedAdditionalFees as getFormattedAdditionalFeesV3,
  getFreeTrialDaysLabel as getFreeTrialDaysLabelV3,
  getPeriodLabel as getPeriodLabelV3,
  getPlanDuration as getPlanDurationV3,
} from '../components/PlanCustomization/Widget/PlanPreview/plan-v3-utils';
import { getFormattedPriceData } from '../hooks';
import { getFormattedAdditionalFees } from '../utils/get-formatted-additional-fees';
import { getFreeTrialDaysLabel } from '../utils/getFreeTrialDaysLabel';
import { getPeriodLabel } from '../utils/getPeriodLabel';
import { getPlanDuration } from '../utils/getPlanValidityCycle';

interface GetTranslatedPricingDetailsContext {
  t: TFunction;
  locale: string | undefined;
  createCurrencyFormatter: CreateCurrencyFormatter;
}

export function createGetTranslatedPricingDetails(
  context: GetTranslatedPricingDetailsContext,
): (plan: PublicPlanV2 | PlanV3) => TranslatedPricingDetails {
  return (plan) =>
    isPlanV3(plan) ? getTranslatedPricingDetailsV3(plan, context) : getTranslatedPricingDetailsV2(plan, context);
}

function getTranslatedPricingDetailsV2(
  plan: PublicPlanV2,
  context: GetTranslatedPricingDetailsContext,
): TranslatedPricingDetails {
  const { value, currency } = getPlanPrice(plan);
  const { t, locale, createCurrencyFormatter } = context;

  const formattedPriceData = getFormattedPriceData({
    createCurrencyFormatter,
    locale,
    value,
    currency,
  });

  return {
    price: formattedPriceData.fullPrice,
    cycle: getPeriodLabel(plan.pricing?.subscription, t) ?? '',
    duration: getPlanDuration(plan, t),
    freeTrial: getFreeTrialDaysLabel(plan, t),
    additionalFees: getFormattedAdditionalFees({
      plan,
      locale,
      createCurrencyFormatter,
    }),
  };
}

function getTranslatedPricingDetailsV3(
  plan: PlanV3,
  context: GetTranslatedPricingDetailsContext,
): TranslatedPricingDetails {
  const { value, currency } = getPlanPriceV3(plan);
  const { t, locale, createCurrencyFormatter } = context;

  const formattedPriceData = getFormattedPriceData({
    createCurrencyFormatter,
    locale,
    value: value?.toString(),
    currency,
  });

  return {
    price: formattedPriceData.fullPrice,
    cycle: getPeriodLabelV3(plan, t) ?? '',
    duration: getPlanDurationV3(plan, t),
    freeTrial: getFreeTrialDaysLabelV3(plan, t),
    additionalFees: getFormattedAdditionalFeesV3({
      plan,
      locale,
      createCurrencyFormatter,
    }),
  };
}
